import React from "react";
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Link, TextField } from "@material-ui/core";
import { RawLinkButton } from "../LinkButton";
import WithLoadingSpinners from "../hoc/WithLoadingSpinner";
import Services from "../../services/Services";
import { isNullOrWhitespace } from "../../functions/stringHandling";
import { DialogContentErrorText } from "../../functions/errorHandling";

export interface ISignUpValidationCodeDialogProps {
  isOpen: boolean;
  email: string;
  validationCode: string;
  cancel: () => void;
  submitServiceCall: () => Promise<void>;
  setFormError: ([]) => void;
  updateValidationCode: (confirmationCode: string) => void;
}

const SignUpVerificationCodeDialog = (props: ISignUpValidationCodeDialogProps) => {
  const { isOpen, email, validationCode, cancel, setFormError, submitServiceCall, updateValidationCode } = props;

  const [showSubmitButton, setShowSubmitButton] = React.useState<boolean>(true);
  const [showLoading, setShowLoading] = React.useState<boolean>(false);
  const [submitError, setSubmitError] = React.useState<string | null>();
  const [codeCreationError, setCodeCreationError] = React.useState<string | null>(null);

  async function handleSubmit() {
    if (!validationCode) {
      setSubmitError(`A confirmation code is required.`);
      return;
    }

    setShowSubmitButton(false);
    setShowLoading(true);

    await submitServiceCall();

    cancel();
  }

  async function createCode(force: boolean) {
    if (isNullOrWhitespace(email)) {
      setFormError(["Email Address is required."]);
      cancel();
      return;
    }

    setShowLoading(true);
    const codeRepo = await new Services().ConfirmationCodeRepository();
    try {
      await codeRepo.createCode(email, force);
    } catch (err) {
      if (err.message) {
        setCodeCreationError(err.message);
      } else {
        setCodeCreationError(`An unknown error occurred while trying to create a verification code. ${err}`);
      }
      setShowLoading(false);
      return;
    }

    setSubmitError(null);
    setCodeCreationError(null);
    setShowLoading(false);
    setShowSubmitButton(true);
  }

  React.useEffect(() => {
    if (isOpen) {
      createCode(false);
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onClose={cancel} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
      <DialogTitle id="alert-dialog-title">Validation Code</DialogTitle>
      <DialogContent>
        <WithLoadingSpinners isLoading={showLoading} message={"Loading"}>
          <DialogContentText>
            A confirmation code has been sent to your email. This code is valid for 10 minutes. To complete your signup application, please enter the code below
            and click Submit.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="confirmationCode"
            value={validationCode}
            type="text"
            fullWidth
            onChange={e => {
              setSubmitError(null);
              updateValidationCode(e.target.value);
            }}
          />
          {submitError && <DialogContentErrorText>{submitError}</DialogContentErrorText>}
          {codeCreationError && (
            <>
              <DialogContentErrorText>{codeCreationError}</DialogContentErrorText>
            </>
          )}
          <DialogActions>
            {showSubmitButton && (
              <RawLinkButton
                onClick={() => {
                  handleSubmit();
                }}
                color="primary"
              >
                Submit
              </RawLinkButton>
            )}
            <RawLinkButton onClick={() => createCode(true)} color="secondary">
              Resend Code
            </RawLinkButton>
            <RawLinkButton onClick={cancel} color="primary">
              Close
            </RawLinkButton>
          </DialogActions>
        </WithLoadingSpinners>
      </DialogContent>
    </Dialog>
  );
};

export default SignUpVerificationCodeDialog;
